import React from 'react';
import { Droplet, Calculator, ArrowRight } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров бассейна
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  poolTypes
}) => {
  // Получаем текущие числовые значения
  const length = parseFloat(formData.length) || 0;
  const width = parseFloat(formData.width) || 0;
  const depth = parseFloat(formData.depth) || 0;
  const diameter = parseFloat(formData.diameter) || 0;
  const waterLevel = parseFloat(formData.waterLevel) || 90;
  
  // Обработчик изменения длины
  const handleLengthChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      length: newValue
    }));
  };
  
  // Обработчик изменения ширины
  const handleWidthChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      width: newValue
    }));
  };
  
  // Обработчик изменения глубины
  const handleDepthChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      depth: newValue,
      averageDepth: prev.bottomSlope === 'flat' ? newValue : prev.averageDepth
    }));
  };
  
  // Обработчик изменения диаметра
  const handleDiameterChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      diameter: newValue
    }));
  };
  
  // Обработчик изменения уровня воды
  const handleWaterLevelChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      waterLevel: newValue
    }));
  };
  
  // Функция расчета
  const calculateVolume = () => {
    // Эмуляция нажатия кнопки расчета в основной форме
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Стандартные размеры бассейнов в метрах
  const standardSizes = [
    { name: 'Малый (3×2×1.2м)', length: '3', width: '2', depth: '1.2' },
    { name: 'Средний (6×3×1.5м)', length: '6', width: '3', depth: '1.5' },
    { name: 'Большой (8×4×1.8м)', length: '8', width: '4', depth: '1.8' },
    { name: 'Круглый малый (Ø3м)', diameter: '3', depth: '1.2' },
    { name: 'Круглый стандарт (Ø4.5м)', diameter: '4.5', depth: '1.5' }
  ];
  
  // Переключение на предустановленный размер
  const setStandardSize = (size) => {
    setFormData(prev => {
      // Определяем тип бассейна (прямоугольный или круглый)
      const isCircular = 'diameter' in size;
      
      // Создаем новое состояние
      const newState = {
        ...prev,
        poolType: isCircular ? 'circular' : 'rectangular',
      };
      
      // Добавляем соответствующие размеры
      if (isCircular) {
        newState.diameter = size.diameter;
        newState.depth = size.depth;
      } else {
        newState.length = size.length;
        newState.width = size.width;
        newState.depth = size.depth;
      }
      
      return newState;
    });
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-blue-500" />
        Быстрый расчет
      </h3>
      
      <div className="space-y-5">
        {/* Стандартные размеры - быстрый выбор */}
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Популярные размеры бассейнов в России:
          </p>
          <div className="grid grid-cols-1 gap-2">
            {standardSizes.map((size, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setStandardSize(size)}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
              >
                {size.name}
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {'length' in size ? `${size.length}×${size.width}×${size.depth}м` : `Ø${size.diameter}×${size.depth}м`}
                </span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Размеры с использованием слайдеров */}
        <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">
            Настройка размеров:
          </h4>
          
          {/* Показываем слайдеры в зависимости от типа бассейна */}
          {formData.poolType === 'rectangular' || formData.poolType === 'oval' ? (
            <>
              {/* Длина */}
              <div>
                <div className="flex justify-between items-center mb-1 text-sm">
                  <label htmlFor="lengthSlider" className="text-gray-600 dark:text-gray-400">Длина</label>
                  <span className="font-medium text-gray-900 dark:text-white">{formData.length} {formData.unitSystem === 'metric' ? 'м' : 'фт'}</span>
                </div>
                <input
                  type="range"
                  id="lengthSlider"
                  min={formData.unitSystem === 'metric' ? "1" : "3"}
                  max={formData.unitSystem === 'metric' ? "20" : "66"}
                  step={formData.unitSystem === 'metric' ? "0.5" : "1"}
                  value={length}
                  onChange={handleLengthChange}
                  aria-label="Длина бассейна"
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
              </div>
              
              {/* Ширина */}
              <div>
                <div className="flex justify-between items-center mb-1 text-sm">
                  <label htmlFor="widthSlider" className="text-gray-600 dark:text-gray-400">Ширина</label>
                  <span className="font-medium text-gray-900 dark:text-white">{formData.width} {formData.unitSystem === 'metric' ? 'м' : 'фт'}</span>
                </div>
                <input
                  type="range"
                  id="widthSlider"
                  min={formData.unitSystem === 'metric' ? "1" : "3"}
                  max={formData.unitSystem === 'metric' ? "10" : "33"}
                  step={formData.unitSystem === 'metric' ? "0.5" : "1"}
                  value={width}
                  onChange={handleWidthChange}
                  aria-label="Ширина бассейна"
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
              </div>
            </>
          ) : formData.poolType === 'circular' ? (
            <>
              {/* Диаметр */}
              <div>
                <div className="flex justify-between items-center mb-1 text-sm">
                  <label htmlFor="diameterSlider" className="text-gray-600 dark:text-gray-400">Диаметр</label>
                  <span className="font-medium text-gray-900 dark:text-white">{formData.diameter} {formData.unitSystem === 'metric' ? 'м' : 'фт'}</span>
                </div>
                <input
                  type="range"
                  id="diameterSlider"
                  min={formData.unitSystem === 'metric' ? "1" : "3"}
                  max={formData.unitSystem === 'metric' ? "10" : "33"}
                  step={formData.unitSystem === 'metric' ? "0.5" : "1"}
                  value={diameter}
                  onChange={handleDiameterChange}
                  aria-label="Диаметр бассейна"
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
              </div>
            </>
          ) : null}
          
          {/* Глубина - для всех типов */}
          <div>
            <div className="flex justify-between items-center mb-1 text-sm">
              <label htmlFor="depthSlider" className="text-gray-600 dark:text-gray-400">Глубина</label>
              <span className="font-medium text-gray-900 dark:text-white">{formData.depth} {formData.unitSystem === 'metric' ? 'м' : 'фт'}</span>
            </div>
            <input
              type="range"
              id="depthSlider"
              min={formData.unitSystem === 'metric' ? "0.5" : "1.5"}
              max={formData.unitSystem === 'metric' ? "3" : "10"}
              step={formData.unitSystem === 'metric' ? "0.1" : "0.5"}
              value={depth}
              onChange={handleDepthChange}
              aria-label="Глубина бассейна"
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>
          
          {/* Уровень заполнения - для всех типов */}
          <div>
            <div className="flex justify-between items-center mb-1 text-sm">
              <label htmlFor="waterLevelSlider" className="text-gray-600 dark:text-gray-400">Уровень заполнения</label>
              <span className="font-medium text-gray-900 dark:text-white">{formData.waterLevel}%</span>
            </div>
            <input
              type="range"
              id="waterLevelSlider"
              min="50"
              max="100"
              step="5"
              value={waterLevel}
              onChange={handleWaterLevelChange}
              aria-label="Уровень заполнения"
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>
        </div>
        
        {/* Выбор типа бассейна */}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
            Форма бассейна:
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {poolTypes.slice(0, 3).map((type) => (
              <button
                key={type.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, poolType: type.id }))}
                className={`p-2 text-xs rounded-lg border transition-colors text-center
                  ${formData.poolType === type.id 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {type.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Быстрое прогнозирование объема */}
        <div className="pt-4 mt-2 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
            Приблизительный объем:
          </h4>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800 mb-3">
            <div className="flex items-start">
              <Droplet className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                  {(() => {
                    // Приблизительный расчет для предпросмотра
                    let approxVolume = 0;
                    if (formData.poolType === 'rectangular') {
                      approxVolume = length * width * depth;
                    } else if (formData.poolType === 'circular') {
                      approxVolume = Math.PI * Math.pow(diameter/2, 2) * depth;
                    } else if (formData.poolType === 'oval') {
                      approxVolume = Math.PI * (length/2) * (width/2) * depth;
                    }
                    approxVolume = approxVolume * (waterLevel / 100);
                    
                    return (
                      <>
                        {formatNumber(approxVolume, 1)} м³ <span className="text-xs font-normal">({formatNumber(approxVolume * 1000, 0)} л)</span>
                      </>
                    );
                  })()}
                </div>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Предварительная оценка. Для точного расчета нажмите кнопку ниже.
                </p>
              </div>
            </div>
          </div>
          
          <Button
            primary
            fullWidth
            onClick={calculateVolume}
            icon={<Calculator className="w-4 h-4" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
          >
            Рассчитать объем
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;